import { GLOBAL_SEARCH_PAGES } from './global-search-pages.catalog';
import { GlobalSearchItem } from './global-search.models';

const FILLER = [
  'please', 'can you', 'could you', 'would you', 'i want to', 'i need to',
  'go to', 'go', 'open', 'show me', 'show', 'take me to', 'navigate to',
  'navigate', 'launch', 'start', 'bring up', 'pull up', 'find', 'search for',
  'search', 'look for', 'look up', 'the', 'a', 'an', 'my', 'page', 'section',
  'screen', 'module', 'menu'
];

/** Spoken aliases → page catalog id */
const VOICE_ALIASES: Array<{ phrases: string[]; pageId: string }> = [
  { phrases: ['qa', 'q a', 'quality assurance', 'quality', 'assurance queue', 'qa queue'], pageId: 'page-qa' },
  { phrases: ['error queue', 'errored reports', 'errored report', 'errors', 'error reports'], pageId: 'page-qa-errors' },
  { phrases: ['order report', 'order reports', 'order a report', 'new order', 'screening order'], pageId: 'page-order-reports' },
  { phrases: ['subscribers', 'subscriber list', 'accounts', 'companies'], pageId: 'page-subscribers' },
  { phrases: ['add subscriber', 'new subscriber', 'create subscriber'], pageId: 'page-add-subscriber' },
  { phrases: ['dashboard', 'home', 'overview'], pageId: 'page-home' },
  { phrases: ['vendor orders', 'vendor', 'tazworks orders'], pageId: 'page-vendor-orders' },
  { phrases: ['report management', 'reports admin'], pageId: 'page-report-management' },
  { phrases: ['screening', 'edit screening', 'screening criteria', 'criminal criteria'], pageId: 'page-edit-screening' },
  { phrases: ['pricing', 'edit pricing', 'pricing criteria'], pageId: 'page-edit-pricing' },
  { phrases: ['payments', 'manage payments', 'wallet', 'billing'], pageId: 'page-manage-payments' },
  { phrases: ['invoices', 'invoice list', 'invoice'], pageId: 'page-invoice-list' },
  { phrases: ['logs', 'system logs', 'audit logs'], pageId: 'page-logs' },
  { phrases: ['eviction', 'eviction data', 'evictions'], pageId: 'page-eviction-data' },
  { phrases: ['settings', 'admin settings', 'configuration', 'config'], pageId: 'page-admin-settings' },
  { phrases: ['api monitor', 'api'], pageId: 'page-api-monitor' },
  { phrases: ['alerts', 'announcements'], pageId: 'page-alerts' },
  { phrases: ['disclosure', 'disclosures', 'disclosure management'], pageId: 'page-disclosure' },
  { phrases: ['users', 'admin users'], pageId: 'page-users' },
  { phrases: ['properties', 'manage properties'], pageId: 'page-manage-properties' }
];

export interface VoiceParseResult {
  raw: string;
  cleaned: string;
  wantsOpen: boolean;
  matchedItem: GlobalSearchItem | null;
  confidence: number;
}

function normalizeTranscript(raw: string): string {
  return String(raw || '')
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function stripVoiceFiller(text: string): string {
  let out = normalizeTranscript(text);
  // Longest fillers first
  const fillers = [...FILLER].sort((a, b) => b.length - a.length);
  for (const f of fillers) {
    const re = new RegExp(`(?:^|\\s)${f.replace(/\s+/g, '\\s+')}(?=\\s|$)`, 'gi');
    out = out.replace(re, ' ');
  }
  return out.replace(/\s+/g, ' ').trim();
}

function findPageById(id: string): GlobalSearchItem | null {
  return GLOBAL_SEARCH_PAGES.find((p) => p.id === id) || null;
}

/**
 * Parse natural speech into either a direct page navigation or a cleaned search query.
 * Examples: "please open qa" → QA page; "quality assurance" → QA page; "find john smith" → search.
 */
export function parseVoiceCommand(transcript: string): VoiceParseResult {
  const raw = String(transcript || '').trim();
  const normalized = normalizeTranscript(raw);
  const wantsOpen = /\b(open|go to|navigate|show|launch|take me|bring up|pull up)\b/i.test(normalized);
  const cleaned = stripVoiceFiller(normalized) || normalized;

  let matchedItem: GlobalSearchItem | null = null;
  let confidence = 0;

  for (const alias of VOICE_ALIASES) {
    for (const phrase of alias.phrases) {
      if (cleaned === phrase || normalized === phrase) {
        matchedItem = findPageById(alias.pageId);
        confidence = 1;
        break;
      }
      if (cleaned.includes(phrase) || normalized.includes(phrase)) {
        const score = phrase.length / Math.max(cleaned.length, phrase.length);
        if (score > confidence) {
          matchedItem = findPageById(alias.pageId);
          confidence = Math.min(0.95, 0.55 + score);
        }
      }
    }
    if (confidence >= 1) {
      break;
    }
  }

  // Boost when user said "open …"
  if (matchedItem && wantsOpen) {
    confidence = Math.min(1, confidence + 0.15);
  }

  return {
    raw,
    cleaned: cleaned || raw,
    wantsOpen,
    matchedItem,
    confidence
  };
}

export function isSpeechRecognitionSupported(): boolean {
  const w = window as any;
  return !!(w.SpeechRecognition || w.webkitSpeechRecognition);
}
